import {
  Button,
  ButtonLink,
  Header,
  Icon,
  Stack,
  StackItem,
  Text
} from '@asgard/ui-library';
import { faCheckCircle as faCheckCircleLight } from '@fortawesome/pro-light-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormSelect } from 'components/form/FormSelect';
import { FormText } from 'components/form/FormText/FormText';
import { HashAnchor } from 'components/HashAnchor';
import Page from 'components/layout/Page';
import Checkbox from 'components/shared/Checkbox';
import HtmlTrans from 'components/shared/HtmlTrans';
import { LinkText } from 'components/shared/LinkText';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-scroll';
import { ServiceLead } from 'services/service.lead';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getErrorMessage } from 'utils/errors';
import * as S from './MagnetManagerPage.styled';
import {
  MagnetManagerForm,
  MagnetManagerFormSchema
} from './MagnetManagerPage.types';
import { getPublicPath } from 'utils/paths';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export const MagnetManagerPage: React.FC = () => {
  const { t } = useI18n('magnets');
  const { addErrorToast } = useToasts();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [terms, setTerms] = useState(false);
  const [accept, setAccept] = useState(false);

  const formMethods = useForm<MagnetManagerForm>({
    mode: 'onBlur',
    defaultValues: {
      fullName: '',
      email: '',
      company: ''
    },
    resolver: zodResolver(MagnetManagerFormSchema)
  });

  const reason = formMethods.watch('reason');

  const onSubmit = useCallback(
    async (data: MagnetManagerForm) => {
      try {
        if (!executeRecaptcha) {
          console.error('Recaptcha not available');
          return;
        }

        const recaptchaToken = await executeRecaptcha(
          'magnets_how_to_be_manager_submit'
        );

        ServiceLead.create({
          clientName: data.fullName,
          contactEmail: data.email,
          companyName: data.company,
          landingName: 'how-to-be-manager', // Note: landing url slug https://www.getmanfred.com/how-to-be-manager
          createPipedriveLead: false /* Temporary campaign stop */,
          isMql: data.reason === '2',
          isSubscribeToNewsletter: true,
          recaptchaToken
        });

        setIsSubmitted(true);
      } catch (ex) {
        console.error(
          `[MagnetHowToManagerPage] Could not send form data. ${ex}`
        );

        addErrorToast(
          t('magnets.manager.form.submit-error', {
            error: getErrorMessage(ex)
          })
        );
      }
    },
    [addErrorToast, t, executeRecaptcha]
  );

  return (
    <Page pageVariant="midnight_light" noFooter>
      <S.StyledWrapperBG>
        <S.StyledWrapper>
          <Stack direction="column" gap="s" className="content">
            <Stack direction="column" gap="s" className="wrapper">
              <Text bold uppercase size="s">
                {t('magnets.manager.content.sub-title')}
              </Text>
              <Header as="h2" size="4xl">
                {t('magnets.manager.content.title')}
              </Header>

              <HtmlTrans ns="magnets" i18nKey="magnets.manager.content.desc" />
            </Stack>
          </Stack>

          <StackItem className="form">
            {!isSubmitted ? (
              <FormProvider {...formMethods}>
                <HashAnchor name="form" />
                <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                  <Stack direction="column" gap="4xl">
                    <FormText
                      name="fullName"
                      formGroup={{
                        label: t('magnets.manager.form.full-name.label')
                      }}
                    />

                    <FormText
                      name="email"
                      formGroup={{
                        label: t('magnets.manager.form.email.label')
                      }}
                    />

                    <FormText
                      name="company"
                      formGroup={{
                        label: t('magnets.manager.form.company.label')
                      }}
                    />

                    <FormSelect
                      name="reason"
                      formGroup={{
                        label: t('magnets.manager.form.reason')
                      }}
                      options={[
                        {
                          label: t('magnets.manager.form.reason0'),
                          value: '0'
                        },
                        {
                          label: t('magnets.manager.form.reason1'),
                          value: '1'
                        },
                        {
                          label: t('magnets.manager.form.reason2'),
                          value: '2'
                        },
                        {
                          label: t('magnets.manager.form.reason3'),
                          value: '3'
                        }
                      ]}
                    />

                    <Checkbox
                      id="accept"
                      checked={accept}
                      onChange={() => setAccept((prev) => !prev)}
                      label={
                        <HtmlTrans
                          ns="magnets"
                          i18nKey={'magnets.manager.form.privacy.label'}
                        />
                      }
                    />

                    <Checkbox
                      id="terms"
                      checked={terms}
                      onChange={() => setTerms((prev) => !prev)}
                      label={
                        <HtmlTrans
                          ns="magnets"
                          i18nKey={'magnets.manager.form.terms.label'}
                          components={{
                            termsLink: (
                              <LinkText
                                href={getAppUrl(APP_URLS.termsOfUse)}
                                target="_blank"
                              />
                            ),

                            privacyLink: (
                              <LinkText
                                href={getAppUrl(APP_URLS.privacyPolicy)}
                                target="_blank"
                              />
                            )
                          }}
                        />
                      }
                    />

                    <div style={{ display: 'none' }}>
                      {formMethods.formState.isValid
                        ? 'valid: true'
                        : 'valid: false'}
                      {terms ? 'terms: true' : 'terms: false'}
                    </div>

                    <StackItem>
                      <Button
                        label={t('magnets.manager.form.submit')}
                        schema="brand2"
                        startIcon={faCheckCircle}
                        size="xs"
                        isLoading={formMethods.formState.isSubmitting}
                        disabled={
                          !terms ||
                          !accept ||
                          !formMethods.formState.isValid ||
                          reason === '0'
                        }
                      />
                    </StackItem>
                  </Stack>
                </form>
              </FormProvider>
            ) : (
              <Stack direction="column" className="success" alignment="center">
                <Icon icon={faCheckCircleLight} size="6xl" />

                <Header as="h2" size="2xl">
                  {t('magnets.manager.success.title')}
                </Header>

                <Text bold size="l">
                  {t('magnets.manager.success.sub-title')}
                </Text>

                <HtmlTrans
                  ns="magnets"
                  i18nKey="magnets.manager.success.desc"
                  components={{
                    teamLink: (
                      <a href="mailto:team@getmanfred.com" target="_blank" />
                    )
                  }}
                />
              </Stack>
            )}
          </StackItem>
        </S.StyledWrapper>
      </S.StyledWrapperBG>
      <S.StyledMktText>
        <Text bold uppercase size="s">
          {t('magnets.manager.content.sub-title')}
        </Text>
        <Header as="h2" size="2xl">
          {t('magnets.manager.mkt.title')}
        </Header>

        <div>
          <HtmlTrans ns="magnets" i18nKey="magnets.manager.mkt.desc" />
        </div>

        <Link to="form">
          <ButtonLink
            label={t('magnets.manager.form.submit')}
            schema="brand2"
            variant="hard"
            startIcon={faCheckCircle}
            size="xs"
          />
        </Link>
      </S.StyledMktText>
      <S.FooterImg>
        <img
          src={getPublicPath(`/images/magnets-manager-footer.png`)}
          alt={t(`magnets.manager.content.title`)}
        />
      </S.FooterImg>
    </Page>
  );
};
